<template>
  <div class="relative w-full cursor-pointer rounded-lg bg-white" @click="$emit('onView')">
    <div
      class="mb-1 h-40 w-full rounded-t-2xl bg-cover bg-center"
      :style="coverImageUrl ? { backgroundImage: `url(${coverImageUrl})` } : undefined"
    />
    <Badge v-if="isPendingEvidence" variant="warning" class="absolute left-2 top-2">
      {{ t('pendingEvidence') }}
    </Badge>
    <div class="mb-8 flex space-x-2 p-2">
      <figure
        class="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-sm font-semibold text-white md:h-6 md:w-6 md:text-base"
      >
        {{ index }}
      </figure>
      <p class="-mt-[2px] w-4/5 break-words">
        {{ damage.description }}
      </p>
      <div
        v-if="showRepairedStatus && damage.status === DamageStatus.REPAIRED"
        class="absolute right-2 top-4 rounded-full bg-success px-2 py-0.5 text-xs text-white"
      >
        {{ t('repaired') }}
      </div>
    </div>
    <p class="text-gray-500/70 absolute bottom-0 right-1 m-2 break-words text-right text-sm">
      {{ createdDate }}
    </p>
  </div>
</template>

<script setup lang="ts">
import Badge from '@/components/Badge.vue';
import { DamageStatus } from '@carvia/ros-client-types';
import type { Damage } from '@/entities/damage.entity';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { computed, toRefs } from 'vue';
import { hasPendingDamageEvidence } from '@/utils/damage-evidence';

const props = defineProps<{
  damage: Damage;
  index?: number;
  showRepairedStatus?: boolean;
}>();
defineEmits<{
  (e: 'onView'): void;
}>();

const { t } = useI18n();

const createdDate = useFormattedDate(computed(() => props.damage.createdDate));

const coverImageUrl = computed(() => props.damage.imageFileUrls?.[0]);
const isPendingEvidence = computed(() => hasPendingDamageEvidence(props.damage));

const { damage } = toRefs(props);
</script>

<i18n lang="json">
{
  "en": {
    "repaired": "Repaired",
    "pendingEvidence": "Waiting for evidence"
  },
  "de": {
    "repaired": "Repariert",
    "pendingEvidence": "Warte auf Nachweise"
  },
  "es": {
    "repaired": "Reparado",
    "pendingEvidence": "Esperando pruebas"
  }
}
</i18n>
